import React from 'react';
import contensis from 'Common/api/contensis';
import { Op, OrderBy } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';

import Metadata from 'Components/Metadata';
import MainLayout from 'Layout/Main';
import Template, { datamap, PageData } from 'Templates/SubjectArea/Subject';

import type { NextPageWithLayout, GetServerSideProps } from 'next';

const Subject: NextPageWithLayout<PageData> = ({
    entry,
    ancestors,
    courses,
    courseListingFilters,
    facilities,
    solentStories,
    news
}) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template
                {...datamap({
                    entry,
                    ancestors,
                    courses,
                    courseListingFilters,
                    facilities,
                    solentStories,
                    news
                })}
            />
        </>
    );
};

Subject.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ params, preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });
    const { subject } = params as { subject: string };

    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: `/subjects/${subject}`,
            entryLinkDepth: 4,
            entryFields: [
                /* commons */
                'title',
                'sys',
                'id',
                'uri',
                'url',
                'value',
                'link',
                'label',
                'ariaLabel',
                'contentTypeId',
                'image',
                'path',
                /* pageMetadata */
                'pageMetadata',
                'customTitle',
                'thumbnail',
                /* content */
                'heroBanner',
                'subjectOverview',
                'video',
                'carouselImages',
                'ksPsHeading',
                'ksPsList',
                'accreditations',
                'accreditationsHeading',
                'courseListingHeading',
                'courseListingCtas',
                'modalText',
                'modalLinkText',
                'industryLinksHeading',
                'industryIntroText',
                'industryLinks',
                'disclaimer',
                'graduateStoriesIntroText',
                'facilitiesHeading',
                'facilitiesIntroText',
                'openDayPromo',
                'buttonCTA',
                'backgroundColour',
                'mediaHubHeading',
                'faQsHeading',
                'accordion',
                'content',
                'stayUpdatedPromo',
                'heading',
                'buttonVariant',
                'backgroundColor',
                'subjectArea',
                ...excludeMetadata
            ]
        });

        if (!resPage.entry) {
            return { error: true };
        }

        const entry = resPage.entry;

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        const recentArticlesQuery = [
            Op.in('sys.contentTypeId', 'newsArticle', 'blogArticle'),
            Op.in('subjectArea', entry.subjectArea.key),
            Op.exists('customTags', false),
            Op.or(Op.exists('isIndependentBlog', false), Op.equalTo('isIndependentBlog', false)),
            Op.exists('sys.slug', true)
        ];

        const resRecentArticles = await search(recentArticlesQuery, {
            pageSize: 4,
            orderBy: OrderBy.desc('publishDate'),
            fields: [
                'sys',
                'title',
                'description',
                'heroImage',
                'subjectArea',
                'publishDate',
                ...excludeMetadata
            ]
        });

        const facilitiesQuery = [
            Op.in('sys.contentTypeId', 'facility'),
            Op.in('subjects', '0/1259/1638/1648', entry.subjectArea.key),
            Op.exists('sys.uri', true)
        ];

        const resFacilities = await search(facilitiesQuery, {
            pageSize: 24,
            linkDepth: 2,
            orderBy: OrderBy.asc('entryTitle'),
            fields: ['sys', 'title', 'entryTitle', 'intro', 'path', 'image', ...excludeMetadata]
        });

        const storiesQuery = [
            Op.in('sys.contentTypeId', 'solentStory'),
            Op.in('subject', entry.subjectArea.key),
            Op.exists('sys.uri', true),
            Op.exists('graduationYear', true)
        ];

        const resStories = await search(storiesQuery, {
            pageSize: 6,
            linkDepth: 2,
            fields: [
                'sys',
                'id',
                'title',
                'uri',
                'listingCardImage',
                'position',
                'student',
                'company',
                'graduationYear',
                'cardImage',
                'courseCardQuote',
                'organisation',
                ...excludeMetadata
            ],
            orderBy: OrderBy.desc('graduationYear')
        });

        const coursesQuery = Op.and(
            Op.or(
                Op.equalTo('sys.contentTypeId', 'courseUndergraduate'),
                Op.equalTo('sys.contentTypeId', 'coursePostgraduate'),
                Op.equalTo('sys.contentTypeId', 'courseApprenticeships'),
                Op.equalTo('sys.contentTypeId', 'courseProfessional'),
                Op.equalTo('sys.contentTypeId', 'courseFoundationYears')
            ),
            Op.equalTo('courseCategory.sys.id', entry.sys.id),
            Op.exists('sys.uri', true)
        );

        const resCourses = await search(coursesQuery, {
            pageSize: 100,
            linkDepth: 2,
            fields: [
                'sys',
                'uri',
                'title',
                'courseCardImage',
                'name',
                'degreeType',
                'courseDescription',
                'state',
                'courseListingFilterOptions',
                'courseOptions',
                'studyLevel',
                'courseCategory'
            ],
            orderBy: OrderBy.asc('name')
        });

        const courseListing = await client.entries.get({
            id: '41443593-ab1a-4c88-a5bf-c96b5e734a39',
            linkDepth: 2,
            fields: [
                'sys',
                'id',
                'title',
                'uri',
                'studyLevelFilter',
                'courseOptionsFilter',
                ...excludeMetadata
            ]
        });

        return {
            entry: entry,
            ancestors,
            courses: resCourses.items,
            courseListingFilters: courseListing,
            facilities: resFacilities.items,
            solentStories: resStories.items,
            news: resRecentArticles.items
        };
    });

    if (error) {
        return { notFound: true };
    } else {
        setCacheHeaders(res, surrogateKeys);
        return { props: data };
    }
};

export default Subject;
